import { Location } from 'react-router';

import { cookieService } from '@services/cookie.service';
import { UseAuthReturn, AuthStatus } from '@hooks/useAuth';
import { User } from '@typings/user';

import { AUTH_ROUTES, APP_ROUTES, AccessLevel } from './types';

interface RedirectResult {
  path: string | null;
  state?: Record<string, unknown>;
  autoGuest?: boolean;
}

export const isUserAuthenticated = (
  user: User | null,
  isAnonymous: boolean,
): boolean => {
  if (!user?.id || isAnonymous) {
    return false;
  }

  const token = cookieService.getAuthToken();
  return !!token;
};

const isShowRoute = (pathname: string): boolean => {
  return (
    pathname.startsWith(APP_ROUTES.SHOW_LIVE) ||
    pathname.startsWith(APP_ROUTES.SHOW_RECORDED) ||
    pathname.startsWith(APP_ROUTES.SHOW_SCHEDULED)
  );
};

export const getRedirectPath = (
  auth: UseAuthReturn,
  accessLevel: AccessLevel,
  location: Location,
  fallbackPath?: string,
): RedirectResult => {
  const { status, canAccessSetPassword, isAvatarGenerated } = auth;

  switch (accessLevel) {
    case AccessLevel.PUBLIC:
      return handlePublicAccess(status);
    case AccessLevel.AUTHENTICATED:
      return handleAuthenticatedAccess(status, location, fallbackPath);
    case AccessLevel.GUEST:
      return handleGuestAccess(status, location);
    case AccessLevel.ONBOARDING:
      return handleOnboardingAccess(status, location, isAvatarGenerated);
    case AccessLevel.PASSWORD_RESET:
      return handlePasswordResetAccess(status, canAccessSetPassword);
    case AccessLevel.AVATAR_FLOW:
      return handleAvatarFlowAccess(status, location);
    default:
      return { path: AUTH_ROUTES.LOGIN };
  }
};

const handlePublicAccess = (status: AuthStatus): RedirectResult => {
  switch (status) {
    case AuthStatus.Unauthenticated:
    case AuthStatus.Guest:
      return { path: null };
    case AuthStatus.EmailNotVerified:
      return { path: AUTH_ROUTES.CONFIRM_EMAIL };
    case AuthStatus.NotFinishedOnboarding:
      return { path: AUTH_ROUTES.ONBOARDING };
    case AuthStatus.Authenticated:
      return { path: APP_ROUTES.HOME };
    default:
      return { path: null };
  }
};

const handleAuthenticatedAccess = (
  status: AuthStatus,
  location: Location,
  fallbackPath?: string,
): RedirectResult => {
  switch (status) {
    case AuthStatus.Unauthenticated:
      return {
        path: fallbackPath || AUTH_ROUTES.LOGIN,
        state: { from: location.pathname },
      };
    case AuthStatus.Guest:
      return {
        path: AUTH_ROUTES.LOGIN,
        state: { from: location.pathname },
      };
    case AuthStatus.EmailNotVerified:
      return { path: AUTH_ROUTES.CONFIRM_EMAIL };
    case AuthStatus.NotFinishedOnboarding:
      return { path: AUTH_ROUTES.ONBOARDING };
    case AuthStatus.Authenticated:
      return { path: null };
    default:
      return { path: AUTH_ROUTES.LOGIN };
  }
};

const handleGuestAccess = (
  status: AuthStatus,
  location: Location,
): RedirectResult => {
  switch (status) {
    case AuthStatus.Guest:
    case AuthStatus.Authenticated:
      return { path: null };
    case AuthStatus.EmailNotVerified:
      return { path: AUTH_ROUTES.CONFIRM_EMAIL };
    case AuthStatus.NotFinishedOnboarding:
      return { path: AUTH_ROUTES.ONBOARDING };
    case AuthStatus.Unauthenticated:
      if (isShowRoute(location.pathname)) {
        return { path: null, autoGuest: true };
      }
      return {
        path: AUTH_ROUTES.LOGIN,
        state: { from: location.pathname },
      };
    default:
      return {
        path: AUTH_ROUTES.LOGIN,
        state: { from: location.pathname },
      };
  }
};

const handleOnboardingAccess = (
  status: AuthStatus,
  location: Location,
  isAvatarGenerated: boolean,
): RedirectResult => {
  switch (status) {
    case AuthStatus.NotFinishedOnboarding:
      return { path: null };
    case AuthStatus.EmailNotVerified:
      if (window.location.pathname === AUTH_ROUTES.CONFIRM_EMAIL) {
        return { path: null };
      }
      return { path: AUTH_ROUTES.CONFIRM_EMAIL };
    case AuthStatus.Unauthenticated:
    case AuthStatus.Guest:
      return { path: AUTH_ROUTES.LOGIN };
    case AuthStatus.Authenticated:
      if (!isAvatarGenerated && location.pathname === AUTH_ROUTES.ONBOARDING) {
        return {
          path: AUTH_ROUTES.ONBOARDING_AVATAR_GENERATION,
          state: { fromOnboarding: true },
        };
      }
      return { path: APP_ROUTES.HOME };
    default:
      return { path: AUTH_ROUTES.LOGIN };
  }
};

const handlePasswordResetAccess = (
  status: AuthStatus,
  canAccessSetPassword: boolean,
): RedirectResult => {
  if (status === AuthStatus.EmailNotVerified) {
    return { path: AUTH_ROUTES.CONFIRM_EMAIL };
  }
  if (status === AuthStatus.NotFinishedOnboarding) {
    return { path: AUTH_ROUTES.ONBOARDING };
  }
  if (canAccessSetPassword) {
    return { path: null };
  }
  return { path: AUTH_ROUTES.LOGIN };
};

const handleAvatarFlowAccess = (
  status: AuthStatus,
  location: Location,
): RedirectResult => {
  const fromOnboarding = location.state?.fromOnboarding;

  switch (status) {
    case AuthStatus.Authenticated:
      if (fromOnboarding) {
        return { path: null };
      }
      return { path: APP_ROUTES.HOME };
    case AuthStatus.NotFinishedOnboarding:
      return { path: AUTH_ROUTES.ONBOARDING };
    case AuthStatus.EmailNotVerified:
      return { path: AUTH_ROUTES.CONFIRM_EMAIL };
    case AuthStatus.Unauthenticated:
    case AuthStatus.Guest:
      return { path: AUTH_ROUTES.LOGIN };
    default:
      return { path: AUTH_ROUTES.LOGIN };
  }
};
